import React from 'react';
import RGL from 'react-grid-layout';
import { Controller, useForm } from 'react-hook-form';

import { Stream } from 'types/stream';
import { Dashboard } from 'types/dashboard';
import { StreamWidget } from 'types/dashboard_widget';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useModal } from 'hooks/useModal';
import { useStream } from 'streams/hooks/useStream';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { StreamSelect } from 'streams/components/StreamSelect/StreamSelect';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

type StreamWidgetFieldValues = {
  name: string;
  stream: Stream;
  autoPlay: boolean;
};

type StreamFormProps = { widget?: StreamWidget; dashboardId: string };

export function StreamForm({ widget, dashboardId }: StreamFormProps) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const { mutate, isLoading: isUpdating } = useUpdateDashboard({
    onSuccess(data) {
      resetForm(data);
      close();
    },
  });

  const { data: stream, isInitialLoading: isLoadingStream } = useStream(
    widget?.streamId,
    { enabled: !!widget }
  );

  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    register,
    reset,
  } = useForm<StreamWidgetFieldValues>({
    defaultValues: {
      name: widget?.name,
      autoPlay: widget?.autoPlay ?? true,
    },
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  React.useEffect(() => {
    reset({ stream });
  }, [reset, stream]);

  function resetForm(updatedDashboard: Dashboard) {
    if (!widget?.id) {
      return;
    }

    const updatedWidget = updatedDashboard.configuration.widgets.find(
      ({ id }) => id === widget.id
    ) as StreamWidget;

    if (!updatedWidget) {
      return;
    }

    reset({ name: updatedWidget.name, autoPlay: updatedWidget.autoPlay });
  }

  function onSubmit({ name, stream, autoPlay }: StreamWidgetFieldValues) {
    if (!dashboard || !stream || !stream.id) {
      return;
    }

    const id = widget?.id || crypto.randomUUID();
    const layout: Omit<RGL.Layout, 'i'> =
      widget?.layout ||
      getNewWidgetLayout(dashboard.configuration.widgets, 4, 2);

    const updatedDashboard = upsertWidget(
      dashboard,
      new StreamWidget(id, name, stream.id, autoPlay, layout)
    );

    mutate({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="stream" />
      <Field label="Name" error={errors.name}>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
        />
      </Field>
      <Field label="Start playing automatically">
        <LightSwitch {...register('autoPlay')} />
      </Field>
      <Field label="Stream" error={errors.stream}>
        <Controller
          name="stream"
          control={control}
          render={({ field: { onChange, value } }) => (
            <StreamSelect
              value={value}
              onChange={onChange}
              queryFilters={{
                stream_types: ['webrtc', 'rtsp'],
              }}
              isLoading={isLoadingStream}
            />
          )}
          rules={{ required: true }}
        />
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating} />
    </form>
  );
}
